import { memo } from "react";
import { Info, ShieldCheck } from "lucide-react";
import type { ConfidenceBreakdown } from "@/lib/intel/confidence";
import { cn } from "@/lib/utils";

function tone(value: number) {
  if (value >= 75) return "text-success";
  if (value >= 50) return "text-amber";
  return "text-danger";
}

function Bar({ label, value, hint }: { label: string; value: number | null; hint: string }) {
  return (
    <div className="min-w-0">
      <div className="flex items-baseline justify-between gap-2">
        <span className="truncate font-mono text-[10px] uppercase tracking-wide text-muted-foreground" title={hint}>
          {label}
        </span>
        {value === null ? (
          <span className="font-mono text-[10px] uppercase text-muted-foreground">n/a</span>
        ) : (
          <span className={cn("font-display text-sm font-semibold tabular-nums", tone(value))}>{value}%</span>
        )}
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-panel">
        {value !== null && (
          <div
            className={cn("h-full rounded-full", value >= 75 ? "bg-success" : value >= 50 ? "bg-amber" : "bg-danger")}
            style={{ width: `${Math.max(2, value)}%` }}
          />
        )}
      </div>
    </div>
  );
}

/** Explains how the headline confidence was derived — never a bare number. */
function ConfidencePanelImpl({ breakdown }: { breakdown: ConfidenceBreakdown }) {
  const uncalculated = breakdown.status !== "CALCULATED";
  return (
    <section className="rounded-lg border border-panel-border bg-panel/30 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-cyan">
          <ShieldCheck className="h-4 w-4" /> Confidence breakdown
        </div>
        <div className="font-mono text-[10px] text-muted-foreground">
          {breakdown.independentSources} independent of {breakdown.totalSources} cited source(s)
        </div>
      </div>

      {uncalculated && (
        <div className="mt-3 rounded-md border border-amber/40 bg-amber/10 p-2 text-xs text-amber">
          No confidence score could be calculated for this investigation
          {breakdown.usableEvidenceCount === 0 ? " — no source returned evidence about this target." : "."}
          {" "}
          <span className="text-muted-foreground">
            {breakdown.coverage.successful} of {breakdown.coverage.selected} source(s) responded.
          </span>
        </div>
      )}

      <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Bar label="Identity" value={breakdown.identity} hint="Are these signals about one entity?" />
        <Bar label="Sources" value={breakdown.source} hint="Independence and reliability of publishers" />
        <Bar label="Evidence" value={breakdown.evidence} hint="Depth and internal consistency of the data" />
        <Bar label="Match" value={breakdown.match} hint="How strongly the evidence matches the stated target" />
      </div>


      {breakdown.factors.length > 0 && (
        <div className="mt-3 grid gap-1.5 sm:grid-cols-2">
          {breakdown.factors.map((f) => (
            <div key={`${f.label}-${f.impact}`} className="flex items-start gap-2 rounded border border-panel-border bg-background/30 px-2 py-1.5">
              <span
                className={cn(
                  "shrink-0 font-mono text-[11px] font-semibold tabular-nums",
                  f.impact >= 0 ? "text-success" : "text-danger",
                )}
              >
                {f.impact > 0 ? `+${f.impact}` : f.impact}
              </span>
              <span className="min-w-0 text-xs">
                <span className="font-medium">{f.label}</span>
                <span className="block text-[11px] text-muted-foreground">{f.detail}</span>
              </span>
            </div>
          ))}
        </div>
      )}

      {breakdown.caveats.length > 0 && (
        <div className="mt-3 rounded-md border border-amber/30 bg-amber/10 p-2 text-xs text-amber">
          <div className="flex items-center gap-1 font-medium">
            <Info className="h-3.5 w-3.5" /> Uncertainty
          </div>
          {breakdown.caveats.map((c) => (
            <div key={c} className="mt-1">• {c}</div>
          ))}
        </div>
      )}
      <div className="mt-2 font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
        Scoring model v{breakdown.modelVersion} · {breakdown.usableEvidenceCount} evidence item(s)
      </div>
    </section>
  );
}

/** Memoised: these panels render large evidence sets and must not re-render on every keystroke. */
export const ConfidencePanel = memo(ConfidencePanelImpl);
